import React, { useEffect, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import locationTrackingService from '../services/locationTrackingService';

const AndroidLocationBridge = ({ children }) => {
  const [isBridgeReady, setIsBridgeReady] = useState(false);
  const [locationPermission, setLocationPermission] = useState(null);
  const [error, setError] = useState(null);
  const [lastLocation, setLastLocation] = useState(null);
  const [isDriver, setIsDriver] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check if user is a driver (userType.id === 5)
  useEffect(() => {
    const checkUserType = () => {
      const userInfo = localStorage.getItem('userInfo');
      const token = localStorage.getItem('token');
      
      if (token && userInfo) {
        setIsLoggedIn(true);
        try {
          const user = JSON.parse(userInfo);
          const driverCheck = user?.userType?.id === 5;
          setIsDriver(driverCheck);
          console.log('User is driver:', driverCheck);
        } catch (error) {
          console.error('Error parsing user info:', error);
          setIsDriver(false);
        }
      } else {
        setIsLoggedIn(false);
        setIsDriver(false);
      }
    };

    // Initial check
    checkUserType();

    // Listen for storage changes (login/logout events)
    const handleStorageChange = () => {
      checkUserType();
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Custom event for same-tab updates
    const handleUserChange = () => {
      checkUserType();
    };
    window.addEventListener('userLoggedIn', handleUserChange);
    window.addEventListener('userLoggedOut', handleUserChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleUserChange);
      window.removeEventListener('userLoggedOut', handleUserChange);
    };
  }, []);

  useEffect(() => {
    const checkAndroidBridge = () => {
      if (window.Android) {
        console.log('Android WebView bridge detected');
        setIsBridgeReady(true);
        
        // Initialize location tracking service
        locationTrackingService.initAndroidBridge();
        
        // Only check permission if user is logged in as a driver
        if (!isLoggedIn || !isDriver) {
          console.log('Skipping location permission check - not a logged-in driver');
          setLocationPermission(true); // Allow app to continue for non-drivers
          return;
        }
        
        // First check if permission was already granted previously
        const storedPermission = localStorage.getItem('locationPermissionGranted');
        if (storedPermission === 'true') {
          setLocationPermission(true);
          return;
        }
        
        // Check initial permission status
        if (window.Android.isLocationPermissionGranted) {
          const permission = window.Android.isLocationPermissionGranted();
          const isGranted = permission === 'true';
          setLocationPermission(isGranted);
          
          // Store permission status in localStorage to avoid re-asking
          if (isGranted) {
            localStorage.setItem('locationPermissionGranted', 'true');
          }
        }
      } else {
        console.log('Not running in Android WebView');
        setIsBridgeReady(true); // Consider it ready for web browser
        
        // Only check permission if user is logged in as a driver
        if (!isLoggedIn || !isDriver) {
          console.log('Skipping location permission check - not a logged-in driver');
          setLocationPermission(true); // Allow app to continue for non-drivers
          return;
        }
        
        // Check browser permission from localStorage first
        const storedPermission = localStorage.getItem('locationPermissionGranted');
        if (storedPermission === 'true') {
          setLocationPermission(true);
        } else if (navigator?.geolocation) {
          // Try to get browser location permission status
          navigator.permissions.query({ name: 'geolocation' })
            .then(permissionStatus => {
              const isGranted = permissionStatus.state === 'granted';
              setLocationPermission(isGranted);
              if (isGranted) {
                localStorage.setItem('locationPermissionGranted', 'true');
              }
            })
            .catch(() => {
              // Fallback check
              navigator.geolocation.getCurrentPosition(
                () => {
                  setLocationPermission(true);
                  localStorage.setItem('locationPermissionGranted', 'true');
                },
                () => setLocationPermission(false)
              );
            });
        }
      }
    };

    // Check for Android bridge only when login status or driver status changes
    if (isBridgeReady) {
      checkAndroidBridge();
    } else {
      // Initial bridge check
      checkAndroidBridge();
    }

    // Set up listener for permission changes
    window.onLocationPermissionResult = (result) => {
      console.log('Location permission changed:', result);
      const isGranted = result === 'true';
      setLocationPermission(isGranted);
      
      // Update localStorage when permission is granted
      if (isGranted) {
        localStorage.setItem('locationPermissionGranted', 'true');
      }
    };

    // Set up listener for location updates
    window.onLocationUpdate = (location) => {
      console.log('Location update received:', location);
      setLastLocation(location);
    };

    return () => {
      // Clean up listeners
      window.onLocationPermissionResult = null;
      window.onLocationUpdate = null;
    };
  }, [isLoggedIn, isDriver]);

  const requestLocationPermission = () => {
    if (window.Android && window.Android.requestLocationPermission) {
      window.Android.requestLocationPermission();
    } else if (navigator?.geolocation) {
      // For browsers, just try to get location which will trigger permission prompt
      navigator.geolocation.getCurrentPosition(
        () => {
          setLocationPermission(true);
          localStorage.setItem('locationPermissionGranted', 'true');
        },
        (error) => {
          console.error('Browser location error:', error);
          setError('Location permission denied. Please enable it in your browser settings.');
        }
      );
    } else {
      setError('Location permission request not available');
    }
  };

  const getCurrentLocation = () => {
    if (window.Android && window.Android.getCurrentLocation) {
      window.Android.getCurrentLocation();
    } else {
      setError('Location services not available');
    }
  };

  if (!isBridgeReady) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Initializing location services...</Typography>
      </Box>
    );
  }

  // Only show permission dialog for logged-in drivers
  if (isLoggedIn && isDriver && locationPermission === false) {
    return (
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
          textAlign: 'center',
          bgcolor: '#f8fafc'
        }}
      >
        <Box 
          sx={{ 
            maxWidth: 500,
            p: 4,
            bgcolor: 'white',
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(4, 64, 125, 0.08)',
            border: '1px solid #e3e8ef'
          }}
        >
          <Typography 
            variant="h5" 
            gutterBottom
            sx={{ 
              color: '#3B82F6',
              fontWeight: 700,
              mb: 2
            }}
          >
            📍 Location Permission Required
          </Typography>
          <Typography 
            paragraph
            sx={{ 
              color: '#64748b',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              mb: 3
            }}
          >
            As a driver, we need your location to track trips and provide better service. 
            Please grant location permission to continue using the app.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={requestLocationPermission}
            sx={{
              py: 1.5,
              px: 4,
              bgcolor: '#3B82F6',
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(4, 64, 125, 0.2)',
              fontWeight: 600,
              fontSize: '1rem',
              "&:hover": {
                bgcolor: '#033464',
                boxShadow: '0 6px 16px rgba(4, 64, 125, 0.3)',
              }
            }}
          >
            Grant Location Permission
          </Button>
          {error && (
            <Typography 
              color="error" 
              sx={{ 
                mt: 2,
                fontSize: '0.85rem',
                fontWeight: 500
              }}
            >
              {error}
            </Typography>
          )}
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block',
              mt: 3,
              color: '#94a3b8',
              fontSize: '0.75rem'
            }}
          >
            You must grant permission to access driver features
          </Typography>
        </Box>
      </Box>
    );
  }

  return children;
};

export default AndroidLocationBridge; 